<template>
  <div>
    <div style="display: flex;justify-content: center;padding: 40px;">
      <img src="@/assets/logo.png" style="width: 100px;margin-right: 50px;">
      <div>
        <h1 style="text-align: center;font-size: 50px;margin-bottom: 0px;margin: auto 0;">CAPmed-BC</h1>
        <p style="color:black; text-align: center;font-size: 14px; margin:0 auto;">cold atmospheric plasma medicine in breast cancer</p>
      </div>
    </div>
    <p style="color:black; font-weight: 500; text-align: left;font-size: 20px; width: 80%; margin:0 auto;">
      CAPmed-BC is a database and a web tool storing and analyzing high-throughput data from multiple levels showing breast cancer cell response to CAP treatment. All data stored are from Dr. Xiaofeng Dai’s lab.
    </p>
    <div style="display: flex;justify-content: space-between;width: 80%;margin: 0 auto;align-items: center;">
      <div class="banner">
        <img src="@/assets/banner1.png" style="width: 100%;" />
      </div>
      <div class="intro">
        <b>What is CAP</b>
        <p>Plasma is the fourth state of matter besides solid, liquid, gas, which comprises of over 99% of the visible universe. It is an ionised gas enriched with biologically and chemically reactive species, including charged electrons and ions, as well as radicals, atoms, and molecules in neutral (e.g., excited) or charged forms, where the electric charge can be positive or negative. In addition to chemical species, plasmas produce electromagnetic radiation, propagating disturbances such as shock waves and heating, among other effects.</p>
        <p>Cold atmospheric plasma (CAP) is partially ionized gas generated at the room temperature and the atmospheric temperature. It is comprised of a cocktail of reactive oxygen and nitrogen species (RONS) such as hydroxyl radical (OH•), hydrogen peroxide (H<sub>2</sub>O<sub>2</sub>), ozone (O<sub>3</sub>), singleton oxygen (O), superoxide (O<sup>2-</sup>), nitric oxide (NO), and nitrite in the form of anion or proton (OONO<sup>-</sup>, ONOOH), which can generate desired biological effects in a controlled manner with a good degree of spatiotemporal resolution.</p>
        <b>Source for CAP generation</b>
        <p>In the laboratory, CAP can be easily generated by applying an electric field to the process gas, typically pure helium or argon, or their mixtures with oxygen. This electric field accelerates electrons and initiates a cascade of chemical reactions that give rise to a diverse range of chemical species. The amount of applied energy, and the type and pressure of the processing gas determine both the speed (and thus the temperature) and the chemistry of this cocktail of species, and as such are commonly used to tune the properties of CAP for a given application. Devices for generating CAP can be categorized into two modes, i.e., direct-discharge and indirect-discharge. In direct-discharged CAP generation source such as direct dielectric barrier discharge (DBD), cell materials are placed between the two electrodes and exposed to plasma discharges directly. In indirect-discharged CAP generation source such as plasma jet and plasma torch, biomaterials are allowed to be placed outside the device that makes the treatment field adjustable. CAP can also be applied in a liquid form, namely plasma-activated medium (PAM), through activating a liquid solution such as cell cultivating medium, PBS and the Ringer’s lactate solution, which is then used for cultivating cells or treating animal materials through injection.</p>
      </div>
    </div>
    <div style="display: flex;justify-content: space-between;width: 80%;margin: 0 auto;align-items: center;">
      <div class="intro">
        <b>Unique biomedical features of CAP</b>
        <p>Different from other onco-therapeutic modalities, CAP can selectively kill transformed cells without harming healthy cells under appropriate dosage, underpinning its unique targeting properties against cancer cells and little adverse effect. Incremental evidence has suggested the feasibility of using CAP for treating cancers ever since its initial application in the field of oncology in 2007. So far, the onco-therapeutic potential of CAP has been demonstrated in a plethora of cancers including breast cancers and in particular triple negative breast cancers (TNBCs) that do not respond to targeted or hormonal therapies commonly used for treating breast cancers.</p>
        <p>In addition to selective targeting of cancer cells, CAP can be used in wound healing, where it decontaminates and stimulates tissue regeneration. When used on biomaterials, CAP treatment can remove biological and chemical contaminants, functionalise, structure, and activate the surface to control cell–surface interactions, for example, to prevent biofouling or stimulate osseointegration, and deposit functional thin films, for example, antimicrobial or drug release coatings.</p>
      </div>
      <div class="banner">
        <img src="@/assets/banner2.png" style="width: 100%;" />
      </div>
    </div>
    <div style="display: flex;justify-content: space-between;width: 80%;margin: 0 auto;align-items: center;">
      <div class="banner">
        <img src="@/assets/banner3.png" style="width: 100%;" />
      </div>
      <div class="intro">
        <b>Clinical use of CAP</b>
        <p>In the clinics, CAP technology may compliment surgery as an adjuvant therapy. Of particular clinical interest is the ability of CAP to penetrate into tissues and effectively target cancer cells that have infiltrated healthy tissue adjacent to the tumour mass. These infiltrating cells are difficult to isolate and remove during the removal of the primary tumour and as a result surgeons often opt for the resection of large margins around the tumour. With CAP, these margins could be selectively cleared of cancer cells without the need to remove large areas of normal tissue.</p>
        <b>Why focusing on breast cancer</b>
        <p>Breast cancer (BC) is one of the most prevalent and malignant cancers in women. Breast cancers can be categorized into four basic subgroups according to the surface expression of estrogen receptor (ER), progesterone receptor (PR) and human epithelial receptor 2 (HER2). Among the various breast cancer subtypes, triple negative breast cancers (TNBCs) are known for their poor clinical outcome and lack of effective therapeutics with little adverse effects as a result of low or no expression of these surface receptors. Given the high heterogeneity of breast cancers, relatively clear classification and pathogenesis, as well as urgent clinical need for resolving TNBCs taking advantages of innovative technologies, this data center stores omics information on breast cancers in response to CAP treatment with a focus laid on TNBCs.</p>
      </div>
    </div>
    <div style="display: flex;justify-content: space-between;width: 80%;margin: 0 auto;align-items: center;">
      <div class="intro">
        <b>Selection of omics data</b>
        <p>The whole transcriptome data (including mRNA, miRNA, lncRNA and circRNA) is included to capture the <b>dynamic response</b> of breast cancer cells to CAP treatment, where 1h and 8h transcriptome profiles were used to represent short-term and long-term responses of transformed cells to CAP-imposed external perturbations. Proteome and phosphorylome are included to investigate the <b>functional activity</b> of breast cancer cells in response to CAP treatment. Lactylome and acetylome are encompassed to explore clues implicating <b>metabolic rewiring</b> of breast cancer cells in response to CAP treatment. Since lactylome and acetylome compete for epigenetic modification of lysines and mark the levels of lactates and acetyl-CoA, lactylome and acetylome landscapes may implicate the fate of pyruvate and metabolic alteration of breast cancer cells in response to CAP. Thus, integrative analysis of information of lactylome and acetylome may identify vital molecules marking metabolic switch of transformed cells that are vulnerable to CAP treatment.</p>
        <p>Data included in CAPmed-BC demonstrated the differences across various omics in TNBC cells before and after CAP treatment. Besides analyzing data at each omics level, a joint analysis of multi-omics data could potentially provide more in-depth integration and greater insights into the underlying mechanisms. We suggested three ways of data analysis or combination with examples in this study, i.e., analyzing the whole transcriptome to learn the short- and long-term dynamic fluctuation of TNBC cells in response to CAP, combining the proteome and phosphorylome to investigate functional variations of TNBC cells after CAP treatment, and coupling the lactylome and acetylome to explore metabolic clues leading to the therapeutic efficacy of CAP in treating TNBC cells. These suggested combinations reflect the rational on the selection of data sources included in CAPmed-BC that could generate unique results for interpretation. Yet, it does not limit the users from choosing other levels of omics data to obtain their desired outcomes.</p>
      </div>
      <div class="banner">
        <img src="@/assets/banner4.png" style="width: 100%;" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HomeView",
  components: {
  },
  data() {
    return {
    };
  },
  methods: {
    handleBannerClick(banner) {
      // 处理按钮点击事件，例如导航到相应链接
      this.$router.push(banner.link);
    },
  },
};
</script>

<style scoped>
.banner {
  width: 52%;
  margin-top: 80px;
  margin-bottom: 80px;
}
.intro {
  color: #222222;
  width: 42%;
  margin-top: 80px;
  margin-bottom: 80px;
  font-size: 17px;
  text-align: justify;
  line-height: 1.8;
  max-height: 525px;
  overflow-y: auto; /* 设置为 auto，只在需要时出现滚动条 */
  box-sizing: border-box;
  scrollbar-width: none; /* 隐藏 Firefox 默认的滚动条 */
}

.intro::-webkit-scrollbar {
  width: 0; /* 隐藏 WebKit 默认的滚动条 */
}

.intro:hover::-webkit-scrollbar {
  width: 8px; /* 仅在鼠标悬停时显示滚动条 */
}

.intro:hover::-webkit-scrollbar-thumb {
  background-color: #cccccc;
  border-radius: 4px;
}

.intro:hover::-webkit-scrollbar-thumb:hover {
  background-color: #aaaaaa;
}

@media (hover: none) {
  /* 在不支持悬停的设备上（如触控屏），始终使用较细的滚动条 */
  .intro {
    scrollbar-width: thin;
    scrollbar-color: #cccccc transparent;
  }

  .intro::-webkit-scrollbar {
    width: 8px;
  }

  .intro::-webkit-scrollbar-thumb {
    background-color: #cccccc;
    border-radius: 4px;
  }
}

</style>